import React, { useState } from "react";
import styled from "styled-components";

const ContactWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-light);
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const ContactSection = styled.section`
  padding: 80px 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled.form`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--text-dark);
  }

  input,
  textarea {
    width: 100%;
    padding: 15px;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: var(--secondary-color);
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 600;
`;

const InfoCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;

  .icon {
    width: 50px;
    height: 50px;
    background: var(--secondary-color);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    font-size: 1.2rem;
  }

  h4 {
    color: var(--text-dark);
    margin-bottom: 10px;
    font-size: 1.1rem;
  }

  p {
    color: var(--text-light);
    margin: 0;
    line-height: 1.6;
  }

  a {
    color: var(--secondary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    // Simulate form submission
    setTimeout(() => {
      console.log("Form submitted:", formData);
      setSubmitStatus("success");
      setIsSubmitting(false);

      // Reset form after success
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(""), 5000);
    }, 1000);
  };

  return (
    <ContactWrapper>
      <HeroSection>
        <Container>
          <HeroTitle>Contact Us</HeroTitle>
          <HeroSubtitle>
            Get in touch with our container experts. We're here to help with all
            your shipping container needs.
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <ContactSection>
        <Container>
          <ContactGrid>
            <div>
              <ContactForm onSubmit={handleSubmit}>
                <h3 style={{ marginBottom: "30px", color: "var(--text-dark)" }}>
                  Send us a Message
                </h3>

                {submitStatus === "success" && (
                  <SuccessMessage>
                    Thank you for your message! We'll get back to you within 24
                    hours.
                  </SuccessMessage>
                )}

                <FormGroup>
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your container needs..."
                    required
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send Message"}
                </SubmitButton>
              </ContactForm>
            </div>

            <div>
              <h3 style={{ marginBottom: "30px", color: "var(--text-dark)" }}>
                Get in Touch
              </h3>

              <InfoCard>
                <div className="icon">
                  <i className="fas fa-phone"></i>
                </div>
                <h4>Phone</h4>
                <p>
                  <a href="tel:+14845429203">(484) 542-9203</a>
                  <br />
                  Call us for immediate assistance
                </p>
              </InfoCard>

              <InfoCard>
                <div className="icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <h4>Email</h4>
                <p>
                  <a href="mailto:sales@baypointstorage.com">
                    sales@baypointstorage.com
                  </a>
                  <br />
                  We'll respond within 24 hours
                </p>
              </InfoCard>

              <InfoCard>
                <div className="icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <h4>Address</h4>
                <p>
                  <br />
                  6401 Geil Ln, Louisville, KY 40219
                </p>
              </InfoCard>

              <InfoCard>
                <div className="icon">
                  <i className="fas fa-handshake"></i>
                </div>
                <h4>Regional Partners</h4>
                <p>
                  23631 US-270, Woodward, OK 73801
                  <br />
                  3260 5th S, Salt Lake City, UT 84104
                  <br />
                  <br />
                  <em>Nationwide delivery available</em>
                </p>
              </InfoCard>

              <InfoCard>
                <div className="icon">
                  <i className="fas fa-clock"></i>
                </div>
                <h4>Business Hours</h4>
                <p>
                  Monday - Friday: 8:00 AM - 6:00 PM
                  <br />
                  Saturday: 9:00 AM - 4:00 PM
                  <br />
                  Sunday: Closed
                </p>
              </InfoCard>
            </div>
          </ContactGrid>
        </Container>
      </ContactSection>
    </ContactWrapper>
  );
};

export default Contact;
